import type { IntlShape } from "@formatjs/intl";
import { Empty, Flex, Table, Tag, Typography } from "antd";
import type { ColumnType } from "antd/es/table";
import * as commonColumns from "../../../common/columns";
import type { TicketMaterialsUsage } from "../../generated-axios-client";
import { measurementUnitLabelMap } from "../ticketConstants";

type Props = {
  intl: IntlShape;
  materials: TicketMaterialsUsage[];
  materialCatalog: Record<string, string>;
  sapCodeCatalog: Record<string, string | null>;
};

export const SuppliesInfo = ({ intl, materials, materialCatalog, sapCodeCatalog }: Props) => {
  const columns: ColumnType<TicketMaterialsUsage>[] = [
    {
      key: "material",
      dataIndex: "material",
      title: intl.formatMessage({ id: "ticket_supplies_column_material" }),
      render: (id: string) => materialCatalog[id] ?? id,
    },
    {
      key: "sap_code",
      dataIndex: "material",
      title: intl.formatMessage({ id: "ticket_supplies_column_sap_code" }),
      render: (id: string) => sapCodeCatalog[id] ?? (
        <Typography.Text type="secondary">—</Typography.Text>
      ),
    },
    {
      key: "amount",
      title: intl.formatMessage({ id: "ticket_supplies_column_amount" }),
      render: (_: unknown, record: TicketMaterialsUsage) => {
        if (record.amount == null) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        const unit = record.measurement_unit
          ? measurementUnitLabelMap[record.measurement_unit]
          : undefined;
        return unit ? `${record.amount} ${unit}` : record.amount;
      },
    },
    {
      key: "batch",
      dataIndex: "batch",
      title: intl.formatMessage({ id: "ticket_supplies_column_batch" }),
      render: (val: string | null | undefined) =>
        val != null ? val : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      key: "status",
      title: intl.formatMessage({ id: "ticket_supplies_column_status" }),
      render: (_: unknown, record: TicketMaterialsUsage) =>
        record.amount != null || record.batch != null ? (
          <Tag color="success">
            {intl.formatMessage({ id: "ticket_supplies_status_used" })}
          </Tag>
        ) : (
          <Tag color="default">
            {intl.formatMessage({ id: "ticket_supplies_status_not_recorded" })}
          </Tag>
        ),
      width: 130,
    },
    {
      key: "created_at",
      dataIndex: "created_at",
      title: intl.formatMessage({ id: "ticket_supplies_column_added" }),
      render: (val: string) => (val ? commonColumns.dateTimeValue(val, intl) : "—"),
      width: 160,
    },
  ];

  if (materials.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: "ticket_supplies_empty" })}
      />
    );
  }

  const usedCount = materials.filter((m) => m.amount != null || m.batch != null).length;

  return (
    <Flex vertical gap="small">
      <Typography.Text type="secondary">
        {intl.formatMessage(
          { id: "ticket_supplies_usage_summary" },
          { used: usedCount, total: materials.length }
        )}
      </Typography.Text>
      <Table
        columns={columns}
        dataSource={materials}
        rowKey={(r) => r.id ?? r.material ?? ""}
        pagination={false}
        size="small"
        scroll={{ x: "max-content" }}
      />
    </Flex>
  );
};
